// Event-bus health (Platform Operations Console P2). Cross-tenant view of the
// database-per-service outbox: drainable backlog, publish-lag (oldest pending),
// 24h throughput, at-cap/stuck rows, and the shared DLQ. Reads the event-processor
// platform endpoint through the gateway (platform-operator scope only).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Row, Space, Statistic, Table, Typography, message,
} from 'antd';
import { ReloadOutlined, ApiOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, StatusTag } from '../../components/console';
import {
  getEventBusHealth, type EventBusHealth, type ServiceOutboxHealth, type LabelCount,
} from '../../services/eventBusService';

const { Text } = Typography;

// Human-friendly age. The publish-lag signal: how long the oldest un-published
// event has been waiting. Small = healthy relay; growing = the relay is stalling.
const fmtAge = (secs: number): string => {
  if (!secs || secs < 1) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) return `${(secs / 3600).toFixed(1)}h`;
  return `${(secs / 86400).toFixed(1)}d`;
};

// Colour the oldest-pending age: token-driven, not raw palette.
const ageColor = (secs: number): string | undefined => {
  if (secs > 1800) return 'var(--color-danger)';
  if (secs > 300) return 'var(--color-warning)';
  return undefined;
};

export const EventBusPage: React.FC = () => {
  const [data, setData] = useState<EventBusHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errored, setErrored] = useState(false);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const d = await getEventBusHealth();
      setData(d);
      setErrored(false);
    } catch {
      setErrored(true);
      if (initial) message.error('Could not load event-bus health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const id = setInterval(() => void load(false), 20000); // auto-refresh
    return () => clearInterval(id);
  }, [load]);

  const serviceColumns: ColumnsType<ServiceOutboxHealth> = [
    { title: 'Service', dataIndex: 'service', render: (s: string) => <Text strong>{s}</Text> },
    {
      title: 'Pending',
      dataIndex: 'pending',
      sorter: (a, b) => a.pending - b.pending,
      render: (n: number) => <span style={n > 0 ? { color: 'var(--color-warning)' } : undefined}>{n}</span>,
    },
    {
      title: 'Stuck',
      dataIndex: 'stuck',
      sorter: (a, b) => a.stuck - b.stuck,
      render: (n: number) => <span style={n > 0 ? { color: 'var(--color-danger)', fontWeight: 600 } : undefined}>{n}</span>,
    },
    {
      title: 'Oldest pending',
      dataIndex: 'oldest_pending_age_seconds',
      sorter: (a, b) => a.oldest_pending_age_seconds - b.oldest_pending_age_seconds,
      render: (n: number) => <span style={{ color: ageColor(n) }}>{fmtAge(n)}</span>,
    },
    { title: 'Published (24h)', dataIndex: 'published_24h', sorter: (a, b) => a.published_24h - b.published_24h },
  ];

  const labelColumns: ColumnsType<LabelCount> = [
    { title: 'Name', dataIndex: 'label', render: (s: string) => <Text code>{s || '—'}</Text> },
    { title: 'Count', dataIndex: 'count', width: 100 },
  ];

  return (
    <div>
      <PageHeader
        title="Event bus"
        subtitle="Outbox backlog, publish-lag and dead-letter queue across all services"
        actions={
          <Space>
            {data && <StatusTag status={data.status} />}
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>
              Refresh
            </Button>
          </Space>
        }
      />

      {errored && !data && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Event-bus health unavailable"
          description="The event-processor platform endpoint did not respond."
        />
      )}

      {loading && !data ? (
        <Card><Text type="secondary">Loading event-bus health…</Text></Card>
      ) : data ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {data.notes && data.notes.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Partial data"
              description={data.notes.join(' · ')}
            />
          )}

          <Card title="Totals" extra={<Text type="secondary">retry cap {data.max_attempts}</Text>}>
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic
                  title="Pending (drainable)"
                  value={data.totals.pending}
                  valueStyle={data.totals.pending ? { color: 'var(--color-warning)' } : undefined}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Stuck (at cap)"
                  value={data.totals.stuck}
                  valueStyle={data.totals.stuck ? { color: 'var(--color-danger)' } : undefined}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Dead-lettered"
                  value={data.totals.dlq}
                  valueStyle={data.totals.dlq ? { color: 'var(--color-danger)' } : undefined}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Published (24h)" value={data.totals.published_24h} />
              </Col>
            </Row>
          </Card>

          <Card title="Per-service outbox" extra={<ApiOutlined />}>
            {data.services.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No outbox data." />
            ) : (
              <Table
                size="small"
                rowKey="service"
                pagination={false}
                dataSource={data.services}
                columns={serviceColumns}
              />
            )}
          </Card>

          <Card title="Dead-letter queue">
            {!data.dlq.available ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="DLQ unavailable." />
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Total"
                      value={data.dlq.total}
                      valueStyle={data.dlq.total ? { color: 'var(--color-danger)' } : undefined}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Last 24h"
                      value={data.dlq.last_24h}
                      valueStyle={data.dlq.last_24h ? { color: 'var(--color-danger)' } : undefined}
                    />
                  </Col>
                </Row>
                {(data.dlq.by_producer.length > 0 || data.dlq.by_event_type.length > 0) && (
                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    {data.dlq.by_producer.length > 0 && (
                      <Col xs={24} md={12}>
                        <Text type="secondary">By producer</Text>
                        <Table
                          size="small"
                          rowKey="label"
                          pagination={false}
                          dataSource={data.dlq.by_producer}
                          columns={labelColumns}
                        />
                      </Col>
                    )}
                    {data.dlq.by_event_type.length > 0 && (
                      <Col xs={24} md={12}>
                        <Text type="secondary">By event type</Text>
                        <Table
                          size="small"
                          rowKey="label"
                          pagination={false}
                          dataSource={data.dlq.by_event_type}
                          columns={labelColumns}
                        />
                      </Col>
                    )}
                  </Row>
                )}
              </>
            )}
          </Card>

          <Text type="secondary" style={{ fontSize: 12 }}>
            Updated {fmtDateTime(data.generated_at)} · consumer-side lag lives on the NATS
            broker and is not shown here; oldest-pending age is the publish-lag signal.
          </Text>
        </Space>
      ) : null}
    </div>
  );
};

export default EventBusPage;
